import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import type { GridRowSelectionModel } from '@mui/x-data-grid';
import { ASSET_STATUS_KEYS } from '@equip/utils';
import { PendingDisposeTab } from '@/components/bulk-dispose/PendingDisposeTab';
import { StockDisposeTab } from '@/components/bulk-dispose/StockDisposeTab';
import { AddManualEntryDialog } from '@/components/bulk-dispose/AddManualEntryDialog';
import { AdjustPendingTypeDialog } from '@/components/bulk-dispose/AdjustPendingTypeDialog';
import { useManualEntryStorage } from '@/components/bulk-dispose/useManualEntryStorage';
import type {
  BulkDisposeConditionCode,
  PendingDisposeRow,
  StockDisposeRow,
} from '@/components/bulk-dispose/types';
import { clampQuantity, formatDateTime, hasSelection } from '@/components/bulk-dispose/utils';
import {
  useBulkDisposeAdjustType,
  useBulkDisposeRecords,
  useCompleteBulkDispose,
  useReturnToInventoryFromDisposal,
  useUpdateBinConditionCode,
  useUpdatePendingTypeChange,
} from '@/hooks';
import { useAssetInventory } from '@/hooks/useAssetInventory';
import { EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL } from '@/utils/iemFilters';
import { LoadingSkeleton } from '@/components/shared/LoadingSkeleton';
import { useNotifications } from '@/stores/useNotificationStore';
import { getErrorMessage } from '@/utils/httpClient';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { printPendingBinQueue } from '@/components/bulk-dispose/pendingBinQueuePrint';
import { multiWordMatch } from '@/utils/multiWordMatch';

type DisposeTab = 'pending' | 'stock';
type ConfirmMode = 'pending-1348' | 'pending-4949' | 'stock-1348' | 'pending-return-to-inventory';

type ConfirmRow = {
  id: string;
  title: string;
  subtitle: string;
};

type ConfirmSelection = {
  mode: ConfirmMode;
  title: string;
  description: string;
  selectedQty: number;
  confirmLabel: string;
  confirmColor: 'warning' | 'primary';
  rows: ConfirmRow[];
};

const formatRecoverable = (
  value: string | null | undefined,
): { label: string; color: 'default' | 'success' | 'warning' } => {
  if (!value) {
    return { label: '—', color: 'default' };
  }

  const normalized = value.trim().toUpperCase();
  if (normalized.includes('NON')) {
    return { label: 'Non-Recoverable', color: 'warning' };
  }
  if (normalized.includes('RECOVERABLE')) {
    return { label: 'Recoverable', color: 'success' };
  }
  return { label: value, color: 'default' };
};

const buildPendingGroupKey = (input: {
  assetCatalogId?: string | null;
  assetName: string;
  assetLin?: string | null;
  assetNsn?: string | null;
  catalogRecoverable?: string | null;
}): string => {
  const catalogId = input.assetCatalogId?.trim();
  if (catalogId) {
    return `catalog:${catalogId}`;
  }

  return [
    'fallback',
    input.assetName.trim().toLowerCase(),
    input.assetLin?.trim().toLowerCase() ?? '',
    input.assetNsn?.trim().toLowerCase() ?? '',
    input.catalogRecoverable?.trim().toLowerCase() ?? '',
  ].join('|');
};

const normalizeServiceType = (
  value: string | null | undefined,
): 'serviceable' | 'nonserviceable' | null => {
  if (!value) {
    return null;
  }

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (normalized === 'serviceable') {
    return 'serviceable';
  }
  if (normalized === 'nonserviceable') {
    return 'nonserviceable';
  }
  return null;
};

const normalizeConditionCode = (
  value: string | null | undefined,
): BulkDisposeConditionCode | null => {
  if (!value) {
    return null;
  }

  const normalized = value.trim().toUpperCase();
  if (normalized === 'H' || normalized === 'A' || normalized === 'B') {
    return normalized;
  }
  return null;
};

const resolveServiceTypeLabel = (serviceableCount: number, nonServiceableCount: number): string => {
  if (serviceableCount > 0 && nonServiceableCount > 0) {
    return 'Mixed Serviceability';
  }
  if (serviceableCount > 0) {
    return 'Serviceable';
  }
  if (nonServiceableCount > 0) {
    return 'Non-Serviceable';
  }
  return 'Unknown';
};

const buildTargetLabel = (row: PendingDisposeRow): string => {
  if (row.pendingTypeChangeMixed) {
    return 'Mixed target catalogs';
  }

  const name = row.pendingTypeChangeTargetName ?? 'Selected catalog';
  const lin = row.pendingTypeChangeTargetLin ?? '—';
  const nsn = row.pendingTypeChangeTargetNsn ?? '—';
  return `${name} (LIN ${lin} • NSN ${nsn})`;
};

export const BulkDisposePage: React.FC = () => {
  const notifications = useNotifications();
  const {
    data: pendingRecords = [],
    isLoading: isPendingLoading,
    isFetching: isPendingFetching,
    error: pendingError,
  } = useBulkDisposeRecords();
  const {
    data: stockRecords = [],
    isLoading: isStockLoading,
    isFetching: isStockFetching,
    error: stockError,
  } = useAssetInventory({
    status_key: `eq.${ASSET_STATUS_KEYS.AVAILABLE}`,
    ...EXCLUDE_PNP_CATALOG_TYPE_KEEP_NULL,
  });
  const {
    mutateAsync: completeDisposal,
    isPending: isCompleting,
    reset: resetCompleteDisposal,
  } = useCompleteBulkDispose();
  const { mutateAsync: updateConditionCode } = useUpdateBinConditionCode();
  const { mutateAsync: updatePendingTypeChange, isPending: isUpdatingPendingTypeChange } =
    useUpdatePendingTypeChange();
  const {
    mutateAsync: adjustPendingType,
    isPending: isAdjustingPendingType,
    reset: resetAdjustPendingType,
  } = useBulkDisposeAdjustType();
  const {
    mutateAsync: returnToInventory,
    isPending: isReturningToInventory,
    reset: resetReturnToInventory,
  } = useReturnToInventoryFromDisposal();

  const [activeTab, setActiveTab] = useState<DisposeTab>('pending');
  const { items: manualEntries, addItem: addManualEntry, removeItem: removeManualEntry } = useManualEntryStorage();
  const [manualEntryDialogOpen, setManualEntryDialogOpen] = useState(false);

  const [pendingAssetNameFilter, setPendingAssetNameFilter] = useState('');
  const [pendingLinNsnFilter, setPendingLinNsnFilter] = useState('');
  const [pendingFromDate, setPendingFromDate] = useState('');
  const [pendingToDate, setPendingToDate] = useState('');
  const [pendingChangesOnly, setPendingChangesOnly] = useState(false);
  const [pendingSelectedRowIds, setPendingSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const [pendingQtyByRow, setPendingQtyByRow] = useState<Record<string, number>>({});
  const [pendingConditionCodeByRow, setPendingConditionCodeByRow] = useState<
    Record<string, BulkDisposeConditionCode>
  >({});
  const [pendingConditionUpdateByRow, setPendingConditionUpdateByRow] = useState<
    Record<string, boolean>
  >({});
  const [adjustTypeRow, setAdjustTypeRow] = useState<PendingDisposeRow | null>(null);

  const [stockSearchFilter, setStockSearchFilter] = useState('');
  const [stockSelectedRowIds, setStockSelectedRowIds] = useState<GridRowSelectionModel>([]);
  const [stockQtyByRow, setStockQtyByRow] = useState<Record<string, number>>({});
  const [stockConditionCodeByRow, setStockConditionCodeByRow] = useState<
    Record<string, BulkDisposeConditionCode>
  >({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSelection, setConfirmSelection] = useState<ConfirmSelection | null>(null);
  const [justificationText, setJustificationText] = useState('');
  const [shipFromText, setShipFromText] = useState('');
  const [shipToText, setShipToText] = useState('');
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [submissionWarning, setSubmissionWarning] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState<string | null>(null);
  const [conditionUpdateError, setConditionUpdateError] = useState<string | null>(null);

  const isSubmitting = isCompleting || isAdjustingPendingType || isUpdatingPendingTypeChange || isReturningToInventory;

  const clearFeedback = useCallback(() => {
    setSubmissionError(null);
    setSubmissionWarning(null);
    setSubmissionSuccess(null);
    setConditionUpdateError(null);
    resetCompleteDisposal();
    resetAdjustPendingType();
    resetReturnToInventory();
  }, [resetAdjustPendingType, resetCompleteDisposal, resetReturnToInventory]);

  useEffect(() => {
    setPendingConditionCodeByRow({});
  }, [pendingRecords]);

  const pendingRows = useMemo<PendingDisposeRow[]>(() => {
    const groupedRows = new Map<string, PendingDisposeRow>();

    pendingRecords.forEach((record) => {
      const key = buildPendingGroupKey({
        assetCatalogId: record.assetCatalogId,
        assetName: record.assetName,
        assetLin: record.assetLin,
        assetNsn: record.assetNsn,
        catalogRecoverable: record.catalogRecoverable,
      });
      const lin = record.assetLin ?? '—';
      const nsn = record.assetNsn ?? '—';
      const recoverable = formatRecoverable(record.catalogRecoverable).label;
      const nextDateMarked = record.dateProcessed ?? null;
      const normalizedServiceType = normalizeServiceType(record.serviceType);
      const normalizedConditionCode = normalizeConditionCode(record.conditionCode);
      const isServiceable = normalizedServiceType === 'serviceable';
      const isNonServiceable = normalizedServiceType === 'nonserviceable';

      const hasPendingTarget = Boolean(record.pendingTypeChangeCatalogId);
      const pendingTargetCatalogId = record.pendingTypeChangeCatalogId ?? null;
      const pendingTargetName = record.pendingTypeChangeTargetName ?? null;
      const pendingTargetLin = record.pendingTypeChangeTargetLin ?? null;
      const pendingTargetNsn = record.pendingTypeChangeTargetNsn ?? null;

      const existing = groupedRows.get(key);
      if (!existing) {
        const serviceableCount = isServiceable ? 1 : 0;
        const nonServiceableCount = isNonServiceable ? 1 : 0;
        groupedRows.set(key, {
          id: key,
          assetName: record.assetName,
          size: record.catalogSize ?? null,
          lin,
          nsn,
          availableCount: 1,
          dateMarked: nextDateMarked,
          serviceType: resolveServiceTypeLabel(serviceableCount, nonServiceableCount),
          serviceableCount,
          nonServiceableCount,
          conditionCode: normalizedConditionCode,
          conditionCodeMixed: false,
          recoverable,
          binIds: [record.id],
          pendingTypeChangeQuantity: hasPendingTarget ? 1 : 0,
          pendingTypeChangeCatalogId: hasPendingTarget ? pendingTargetCatalogId : null,
          pendingTypeChangeTargetName: hasPendingTarget ? pendingTargetName : null,
          pendingTypeChangeTargetLin: hasPendingTarget ? pendingTargetLin : null,
          pendingTypeChangeTargetNsn: hasPendingTarget ? pendingTargetNsn : null,
          pendingTypeChangeMixed: false,
          pendingTypeChangeBinIds: hasPendingTarget ? [record.id] : [],
          assetStatusKey: record.assetStatusKey ?? null,
        });
        return;
      }

      existing.availableCount += 1;
      existing.binIds.push(record.id);

      // Track mixed asset statuses within the group
      if (existing.assetStatusKey !== null && existing.assetStatusKey !== (record.assetStatusKey ?? null)) {
        existing.assetStatusKey = null; // mixed
      }

      if (isServiceable) {
        existing.serviceableCount += 1;
      }
      if (isNonServiceable) {
        existing.nonServiceableCount += 1;
      }
      existing.serviceType = resolveServiceTypeLabel(
        existing.serviceableCount,
        existing.nonServiceableCount,
      );

      if (hasPendingTarget) {
        existing.pendingTypeChangeQuantity += 1;
        existing.pendingTypeChangeBinIds.push(record.id);

        if (!existing.pendingTypeChangeCatalogId) {
          existing.pendingTypeChangeCatalogId = pendingTargetCatalogId;
          existing.pendingTypeChangeTargetName = pendingTargetName;
          existing.pendingTypeChangeTargetLin = pendingTargetLin;
          existing.pendingTypeChangeTargetNsn = pendingTargetNsn;
        } else if (existing.pendingTypeChangeCatalogId !== pendingTargetCatalogId) {
          existing.pendingTypeChangeMixed = true;
        }
      }

      const existingTimestamp = existing.dateMarked ? Date.parse(existing.dateMarked) : NaN;
      const nextTimestamp = nextDateMarked ? Date.parse(nextDateMarked) : NaN;
      if (
        !Number.isNaN(nextTimestamp) &&
        (Number.isNaN(existingTimestamp) || nextTimestamp > existingTimestamp)
      ) {
        existing.dateMarked = nextDateMarked;
      }

      if (existing.lin === '—' && lin !== '—') {
        existing.lin = lin;
      }
      if (existing.nsn === '—' && nsn !== '—') {
        existing.nsn = nsn;
      }
      if (existing.recoverable === '—' && recoverable !== '—') {
        existing.recoverable = recoverable;
      }

      if (
        !existing.conditionCodeMixed &&
        existing.conditionCode !== normalizedConditionCode
      ) {
        existing.conditionCode = null;
        existing.conditionCodeMixed = true;
      }
    });

    return Array.from(groupedRows.values())
      .sort((a, b) => a.assetName.localeCompare(b.assetName))
      .map((row) => {
        const override = pendingConditionCodeByRow[row.id];
        if (!override) {
          return row;
        }

        return {
          ...row,
          conditionCode: override,
          conditionCodeMixed: false,
        } satisfies PendingDisposeRow;
      });
  }, [pendingConditionCodeByRow, pendingRecords]);

  const manualPendingRows = useMemo<PendingDisposeRow[]>(
    () =>
      manualEntries.map((item) => ({
        id: `manual:${item.id}`,
        assetName: item.nomenclature,
        size: null,
        lin: item.lin,
        nsn: item.nsn || '—',
        availableCount: item.quantity,
        dateMarked: item.createdAt,
        serviceType: 'Manual Entry',
        serviceableCount: 0,
        nonServiceableCount: 0,
        conditionCode: item.conditionCode,
        conditionCodeMixed: false,
        recoverable: '—',
        binIds: [],
        pendingTypeChangeQuantity: 0,
        pendingTypeChangeCatalogId: null,
        pendingTypeChangeTargetName: null,
        pendingTypeChangeTargetLin: null,
        pendingTypeChangeTargetNsn: null,
        pendingTypeChangeMixed: false,
        pendingTypeChangeBinIds: [],
        assetStatusKey: null,
      })),
    [manualEntries],
  );

  const allPendingRows = useMemo(
    () => [...pendingRows, ...manualPendingRows],
    [pendingRows, manualPendingRows],
  );

  const filteredPendingRows = useMemo(() => {
    const assetNameQuery = pendingAssetNameFilter.trim().toLowerCase();
    const linNsnQuery = pendingLinNsnFilter.trim().toLowerCase();

    return allPendingRows.filter((row) => {
      if (pendingChangesOnly && row.pendingTypeChangeQuantity <= 0) {
        return false;
      }

      if (assetNameQuery && !row.assetName.toLowerCase().includes(assetNameQuery)) {
        return false;
      }

      if (linNsnQuery) {
        const haystack = `${row.lin} ${row.nsn}`.toLowerCase();
        if (!haystack.includes(linNsnQuery)) {
          return false;
        }
      }

      if (pendingFromDate || pendingToDate) {
        const markedDate = row.dateMarked?.slice(0, 10) ?? '';
        if (!markedDate) {
          return false;
        }
        if (pendingFromDate && markedDate < pendingFromDate) {
          return false;
        }
        if (pendingToDate && markedDate > pendingToDate) {
          return false;
        }
      }

      return true;
    });
  }, [
    allPendingRows,
    pendingAssetNameFilter,
    pendingChangesOnly,
    pendingFromDate,
    pendingLinNsnFilter,
    pendingToDate,
  ]);

  const stockRows = useMemo<StockDisposeRow[]>(() => {
    return stockRecords
      .map((row) => {
        const assetIds = row.asset_ids.filter(
          (value): value is string => typeof value === 'string',
        );
        const quantityFromData = Number.isFinite(row.stock_quantity)
          ? Math.max(0, Math.floor(row.stock_quantity))
          : assetIds.length;
        const availableCount =
          assetIds.length > 0 ? Math.min(quantityFromData, assetIds.length) : quantityFromData;

        const locationPath = row.location_path?.trim();
        const locationName = row.location_name?.trim() || '—';
        const location = locationPath ? `${locationName} • ${locationPath}` : locationName;

        return {
          id: row.id,
          assetName: row.asset_name,
          lin: row.lin ?? '—',
          nsn: row.nsn ?? '—',
          location,
          availableCount,
          assetIds,
          conditionCode: stockConditionCodeByRow[row.id] ?? null,
        } satisfies StockDisposeRow;
      })
      .filter((row) => row.availableCount > 0)
      .sort((a, b) => a.assetName.localeCompare(b.assetName));
  }, [stockConditionCodeByRow, stockRecords]);

  const filteredStockRows = useMemo(() => {
    const query = stockSearchFilter.trim().toLowerCase();
    if (!query) {
      return stockRows;
    }

    return stockRows.filter((row) => {
      const haystack = `${row.assetName} ${row.lin} ${row.nsn} ${row.location}`.toLowerCase();
      return multiWordMatch(haystack, query);
    });
  }, [stockRows, stockSearchFilter]);

  const pendingRowsById = useMemo(
    () => new Map(filteredPendingRows.map((row) => [row.id, row])),
    [filteredPendingRows],
  );
  const stockRowsById = useMemo(
    () => new Map(filteredStockRows.map((row) => [row.id, row])),
    [filteredStockRows],
  );

  const pendingSelectedRows = useMemo(
    () => filteredPendingRows.filter((row) => hasSelection(pendingSelectedRowIds, row.id)),
    [filteredPendingRows, pendingSelectedRowIds],
  );

  const pendingSelectedRowsWithPending = useMemo(
    () => pendingSelectedRows.filter((row) => row.pendingTypeChangeQuantity > 0),
    [pendingSelectedRows],
  );

  const pendingSelectedRowsWithoutPending = useMemo(
    () => pendingSelectedRows.filter((row) => row.pendingTypeChangeQuantity <= 0),
    [pendingSelectedRows],
  );

  const pendingSelectedQty = useMemo(() => {
    return pendingSelectedRows.reduce((sum, row) => {
      const qty = clampQuantity(pendingQtyByRow[row.id] ?? row.availableCount, row.availableCount);
      return sum + qty;
    }, 0);
  }, [pendingQtyByRow, pendingSelectedRows]);

  const pendingSelectedPendingTypeChangeQty = useMemo(() => {
    return pendingSelectedRowsWithPending.reduce(
      (sum, row) => sum + row.pendingTypeChangeQuantity,
      0,
    );
  }, [pendingSelectedRowsWithPending]);

  const pendingTotalQty = useMemo(
    () => filteredPendingRows.reduce((sum, row) => sum + row.availableCount, 0),
    [filteredPendingRows],
  );

  const stockSelectedQty = useMemo(() => {
    return filteredStockRows.reduce((sum, row) => {
      if (!hasSelection(stockSelectedRowIds, row.id)) {
        return sum;
      }
      const qty = clampQuantity(stockQtyByRow[row.id] ?? row.availableCount, row.availableCount);
      return sum + qty;
    }, 0);
  }, [filteredStockRows, stockQtyByRow, stockSelectedRowIds]);

  const stockTotalQty = useMemo(
    () => filteredStockRows.reduce((sum, row) => sum + row.availableCount, 0),
    [filteredStockRows],
  );

  const pendingDisposeConfirmRows = useMemo<ConfirmRow[]>(() => {
    return pendingSelectedRowsWithoutPending
      .map((row) => {
        const qty = clampQuantity(
          pendingQtyByRow[row.id] ?? row.availableCount,
          row.availableCount,
        );
        if (qty <= 0) {
          return null;
        }
        return {
          id: row.id,
          title: row.assetName,
          subtitle: `LIN ${row.lin} • NSN ${row.nsn} • Qty ${qty} • Date Marked ${formatDateTime(row.dateMarked)}`,
        };
      })
      .filter((row): row is ConfirmRow => Boolean(row));
  }, [pendingQtyByRow, pendingSelectedRowsWithoutPending]);

  const pendingAdjustConfirmRows = useMemo<ConfirmRow[]>(() => {
    return pendingSelectedRowsWithPending.map((row) => ({
      id: row.id,
      title: row.assetName,
      subtitle: `LIN ${row.lin} • NSN ${row.nsn} • Qty ${row.pendingTypeChangeQuantity} • Target ${buildTargetLabel(row)}`,
    }));
  }, [pendingSelectedRowsWithPending]);

  const stockConfirmRows = useMemo<ConfirmRow[]>(() => {
    return filteredStockRows
      .filter((row) => hasSelection(stockSelectedRowIds, row.id))
      .map((row) => {
        const qty = clampQuantity(stockQtyByRow[row.id] ?? row.availableCount, row.availableCount);
        if (qty <= 0) {
          return null;
        }
        return {
          id: row.id,
          title: row.assetName,
          subtitle: `LIN ${row.lin} • NSN ${row.nsn} • Location ${row.location} • Qty ${qty}`,
        };
      })
      .filter((row): row is ConfirmRow => Boolean(row));
  }, [filteredStockRows, stockQtyByRow, stockSelectedRowIds]);

  const handlePendingQtyChange = useCallback(
    (row: PendingDisposeRow, rawValue: string) => {
      clearFeedback();
      const numericValue = Number(rawValue);
      const normalizedQty = clampQuantity(
        Number.isNaN(numericValue) ? 0 : numericValue,
        row.availableCount,
      );

      setPendingQtyByRow((previous) => {
        if (normalizedQty <= 0) {
          if (!(row.id in previous)) {
            return previous;
          }
          const next = { ...previous };
          delete next[row.id];
          return next;
        }

        if (previous[row.id] === normalizedQty) {
          return previous;
        }
        return {
          ...previous,
          [row.id]: normalizedQty,
        };
      });

      setPendingSelectedRowIds((previous) => {
        const next = new Set(previous.map((value) => String(value)));
        if (normalizedQty > 0) {
          next.add(row.id);
        } else {
          next.delete(row.id);
        }
        return Array.from(next);
      });
    },
    [clearFeedback],
  );

  const handlePendingConditionCodeChange = useCallback(
    async (row: PendingDisposeRow, conditionCode: BulkDisposeConditionCode) => {
      setConditionUpdateError(null);
      const previousConditionCode = row.conditionCode;

      setPendingConditionCodeByRow((previous) => ({
        ...previous,
        [row.id]: conditionCode,
      }));
      setPendingConditionUpdateByRow((previous) => ({
        ...previous,
        [row.id]: true,
      }));

      try {
        await updateConditionCode({
          binIds: row.binIds,
          conditionCode,
        });
      } catch (mutationError) {
        setConditionUpdateError(getErrorMessage(mutationError));
        setPendingConditionCodeByRow((previous) => {
          const next = { ...previous };
          if (previousConditionCode) {
            next[row.id] = previousConditionCode;
          } else {
            delete next[row.id];
          }
          return next;
        });
      } finally {
        setPendingConditionUpdateByRow((previous) => {
          if (!(row.id in previous)) {
            return previous;
          }
          const next = { ...previous };
          delete next[row.id];
          return next;
        });
      }
    },
    [updateConditionCode],
  );

  const handleStockQtyChange = useCallback(
    (row: StockDisposeRow, rawValue: string) => {
      clearFeedback();
      const numericValue = Number(rawValue);
      const normalizedQty = clampQuantity(
        Number.isNaN(numericValue) ? 0 : numericValue,
        row.availableCount,
      );

      setStockQtyByRow((previous) => {
        if (normalizedQty <= 0) {
          if (!(row.id in previous)) {
            return previous;
          }
          const next = { ...previous };
          delete next[row.id];
          return next;
        }

        if (previous[row.id] === normalizedQty) {
          return previous;
        }
        return {
          ...previous,
          [row.id]: normalizedQty,
        };
      });

      setStockSelectedRowIds((previous) => {
        const next = new Set(previous.map((value) => String(value)));
        if (normalizedQty > 0) {
          next.add(row.id);
        } else {
          next.delete(row.id);
        }
        return Array.from(next);
      });
    },
    [clearFeedback],
  );

  const handleStockConditionCodeChange = useCallback(
    (row: StockDisposeRow, conditionCode: BulkDisposeConditionCode) => {
      clearFeedback();
      setStockConditionCodeByRow((previous) => ({
        ...previous,
        [row.id]: conditionCode,
      }));
    },
    [clearFeedback],
  );

  const handlePendingSelectionChange = useCallback(
    (selection: GridRowSelectionModel) => {
      clearFeedback();

      const normalizedSelection = Array.from(
        new Set(selection.map((value) => String(value)).filter((id) => pendingRowsById.has(id))),
      );
      const nextSelectionSet = new Set(normalizedSelection);
      const previousSelectionSet = new Set(pendingSelectedRowIds.map((value) => String(value)));

      setPendingQtyByRow((previous) => {
        let changed = false;
        const next = { ...previous };

        previousSelectionSet.forEach((id) => {
          if (!nextSelectionSet.has(id) && id in next) {
            delete next[id];
            changed = true;
          }
        });

        nextSelectionSet.forEach((id) => {
          if (previousSelectionSet.has(id)) {
            return;
          }
          const row = pendingRowsById.get(id);
          if (!row) {
            return;
          }
          const existingQty = clampQuantity(next[id] ?? 0, row.availableCount);
          const nextQty = existingQty > 0 ? existingQty : row.availableCount;
          if (nextQty > 0 && next[id] !== nextQty) {
            next[id] = nextQty;
            changed = true;
          }
        });

        return changed ? next : previous;
      });

      setPendingSelectedRowIds(normalizedSelection);
    },
    [clearFeedback, pendingRowsById, pendingSelectedRowIds],
  );

  const handlePendingSelectAllVisible = useCallback(() => {
    clearFeedback();
    const allVisibleIds = filteredPendingRows.map((row) => row.id);
    setPendingSelectedRowIds(allVisibleIds);
    setPendingQtyByRow(() => {
      const next: Record<string, number> = {};
      filteredPendingRows.forEach((row) => {
        next[row.id] = row.availableCount;
      });
      return next;
    });
  }, [clearFeedback, filteredPendingRows]);

  const handlePendingDeselectAll = useCallback(() => {
    clearFeedback();
    setPendingSelectedRowIds([]);
    setPendingQtyByRow({});
  }, [clearFeedback]);

  const handleStockSelectionChange = useCallback(
    (selection: GridRowSelectionModel) => {
      clearFeedback();

      const normalizedSelection = Array.from(
        new Set(selection.map((value) => String(value)).filter((id) => stockRowsById.has(id))),
      );
      const nextSelectionSet = new Set(normalizedSelection);
      const previousSelectionSet = new Set(stockSelectedRowIds.map((value) => String(value)));

      setStockQtyByRow((previous) => {
        let changed = false;
        const next = { ...previous };

        previousSelectionSet.forEach((id) => {
          if (!nextSelectionSet.has(id) && id in next) {
            delete next[id];
            changed = true;
          }
        });

        nextSelectionSet.forEach((id) => {
          if (previousSelectionSet.has(id)) {
            return;
          }
          const row = stockRowsById.get(id);
          if (!row) {
            return;
          }
          const existingQty = clampQuantity(next[id] ?? 0, row.availableCount);
          const nextQty = existingQty > 0 ? existingQty : row.availableCount;
          if (nextQty > 0 && next[id] !== nextQty) {
            next[id] = nextQty;
            changed = true;
          }
        });

        return changed ? next : previous;
      });

      setStockSelectedRowIds(normalizedSelection);
    },
    [clearFeedback, stockRowsById, stockSelectedRowIds],
  );

  const handleOpenAdjustType = useCallback(
    (row: PendingDisposeRow) => {
      clearFeedback();
      setAdjustTypeRow(row);
    },
    [clearFeedback],
  );

  const handlePrintPendingQueue = useCallback(() => {
    if (filteredPendingRows.length === 0) {
      notifications.warning(
        'Nothing to print',
        'No items available in the pending bin queue to print.',
      );
      return;
    }

    console.log('Print button clicked - rows:', filteredPendingRows.length);

    try {
      printPendingBinQueue({
        rows: filteredPendingRows,
        filters: {
          assetName: pendingAssetNameFilter,
          linNsn: pendingLinNsnFilter,
          fromDate: pendingFromDate,
          toDate: pendingToDate,
          pendingChangesOnly,
        },
      });
    } catch (error) {
      console.error('Print error:', error);
      notifications.error(
        'Print failed',
        error instanceof Error ? error.message : 'Unable to generate print preview.',
      );
    }
  }, [
    filteredPendingRows,
    pendingAssetNameFilter,
    pendingLinNsnFilter,
    pendingFromDate,
    pendingToDate,
    pendingChangesOnly,
    notifications,
  ]);

  const handleSavePendingTypeChange = useCallback(
    async (input: { row: PendingDisposeRow; targetCatalogId: string; quantity: number }) => {
      clearFeedback();
      await updatePendingTypeChange({
        binIds: input.row.binIds,
        targetCatalogId: input.targetCatalogId,
        quantity: input.quantity,
      });
      setSubmissionSuccess('Pending type change saved.');
    },
    [clearFeedback, updatePendingTypeChange],
  );

  const handleClearPendingTypeChange = useCallback(
    async (row: PendingDisposeRow) => {
      clearFeedback();
      await updatePendingTypeChange({
        binIds: row.binIds,
        targetCatalogId: null,
      });
      setSubmissionSuccess('Pending type change cleared.');
    },
    [clearFeedback, updatePendingTypeChange],
  );

  const handleCloseConfirm = useCallback(() => {
    setConfirmOpen(false);
    setConfirmSelection(null);
    setJustificationText('');
    setShipFromText('');
    setShipToText('');
  }, []);

  const handleOpenPending4949Confirm = useCallback(() => {
    if (pendingSelectedPendingTypeChangeQty <= 0 || pendingAdjustConfirmRows.length === 0) {
      clearFeedback();
      setSubmissionError('Select at least one row with a pending type change to generate 4949.');
      return;
    }

    clearFeedback();
    setConfirmSelection({
      mode: 'pending-4949',
      title: 'Confirm Generate 4949',
      description: `${pendingSelectedPendingTypeChangeQty} item${pendingSelectedPendingTypeChangeQty === 1 ? '' : 's'} will be converted to the selected target types and remain in the pending bin queue.`,
      selectedQty: pendingSelectedPendingTypeChangeQty,
      confirmLabel: 'Confirm',
      confirmColor: 'primary',
      rows: pendingAdjustConfirmRows,
    });
    setConfirmOpen(true);
  }, [clearFeedback, pendingAdjustConfirmRows, pendingSelectedPendingTypeChangeQty]);

  const handleOpenPending1348Confirm = useCallback(() => {
    if (pendingSelectedRowsWithPending.length > 0) {
      clearFeedback();
      setSubmissionError(
        'Generate 1348 is blocked for selected rows with pending type changes. Process them via Generate 4949 or clear the pending type changes first.',
      );
      return;
    }

    if (pendingSelectedQty <= 0 || pendingDisposeConfirmRows.length === 0) {
      clearFeedback();
      setSubmissionError('Select at least one pending item to generate DD 1348-1A.');
      return;
    }

    clearFeedback();
    setConfirmSelection({
      mode: 'pending-1348',
      title: 'Confirm Generate 1348',
      description: `${pendingSelectedQty} item${pendingSelectedQty === 1 ? '' : 's'} will be finalized as disposed.`,
      selectedQty: pendingSelectedQty,
      confirmLabel: 'Confirm',
      confirmColor: 'warning',
      rows: pendingDisposeConfirmRows,
    });
    setConfirmOpen(true);
  }, [
    clearFeedback,
    pendingDisposeConfirmRows,
    pendingSelectedQty,
    pendingSelectedRowsWithPending.length,
  ]);

  const handleOpenReturnToInventoryConfirm = useCallback(() => {
    if (pendingSelectedQty <= 0 || pendingDisposeConfirmRows.length === 0) {
      clearFeedback();
      setSubmissionError('Select at least one pending item to return to inventory.');
      return;
    }

    clearFeedback();
    setConfirmSelection({
      mode: 'pending-return-to-inventory',
      title: 'Confirm Return to Inventory',
      description: `${pendingSelectedQty} item${pendingSelectedQty === 1 ? '' : 's'} will be removed from the disposal queue and returned to available inventory.`,
      selectedQty: pendingSelectedQty,
      confirmLabel: 'Return to Inventory',
      confirmColor: 'primary',
      rows: pendingDisposeConfirmRows,
    });
    setConfirmOpen(true);
  }, [clearFeedback, pendingDisposeConfirmRows, pendingSelectedQty]);

  const handleOpenStock1348Confirm = useCallback(() => {
    if (stockSelectedQty <= 0 || stockConfirmRows.length === 0) {
      clearFeedback();
      setSubmissionError('Select at least one in-stock item to generate DD 1348-1A.');
      return;
    }

    clearFeedback();
    setConfirmSelection({
      mode: 'stock-1348',
      title: 'Confirm Generate 1348',
      description: `${stockSelectedQty} item${stockSelectedQty === 1 ? '' : 's'} will be finalized as disposed.`,
      selectedQty: stockSelectedQty,
      confirmLabel: 'Confirm',
      confirmColor: 'warning',
      rows: stockConfirmRows,
    });
    setConfirmOpen(true);
  }, [clearFeedback, stockConfirmRows, stockSelectedQty]);

  const handleConfirm = useCallback(async () => {
    if (!confirmSelection) {
      return;
    }

    clearFeedback();

    // Pre-open a window synchronously during the user gesture so the browser
    // does not block the popup after the async API call completes.
    const da4949Window = confirmSelection.mode === 'pending-4949' ? preOpenWindow() : null;
    const dd1348Window =
      confirmSelection.mode === 'pending-1348' || confirmSelection.mode === 'stock-1348'
        ? preOpenWindow()
        : null;

    try {
      if (confirmSelection.mode === 'pending-4949') {
        const selectedBinIds = Array.from(
          new Set(pendingSelectedRowsWithPending.flatMap((row) => row.pendingTypeChangeBinIds)),
        );

        if (!selectedBinIds.length) {
          handleCloseConfirm();
          if (da4949Window && !da4949Window.closed) da4949Window.close();
          const message =
            'Unable to resolve selected pending type-change bins. Refresh and try again.';
          setSubmissionError(message);
          notifications.error('Generate 4949 failed', message);
          return;
        }

        const result = await adjustPendingType({
          binIds: selectedBinIds,
          justification: justificationText.trim() || undefined,
        });
        handleCloseConfirm();
        setPendingSelectedRowIds([]);
        setPendingQtyByRow({});
        const warningMessages = Array.isArray(result.warnings)
          ? result.warnings.filter(
              (message): message is string =>
                typeof message === 'string' && message.trim().length > 0,
            )
          : [];
        if (!result.transactionLogId) {
          warningMessages.push(
            'Transaction log entry could not be created. Type adjustment still completed.',
          );
        }
        if (result.da4949GeneratedDocumentId) {
          await openDocumentWithAuth(
            `/api/v1/documents/${result.da4949GeneratedDocumentId}`,
            da4949Window,
          );
        } else {
          if (da4949Window && !da4949Window.closed) da4949Window.close();
          warningMessages.push(
            'DA Form 4949 was not generated/linked. Type adjustment still completed.',
          );
        }
        if (warningMessages.length > 0) {
          const warningText = Array.from(new Set(warningMessages)).join(' ');
          setSubmissionWarning(warningText);
          notifications.warning('Generate 4949 completed with warnings', warningText);
        }
        const docSuffix = result.da4949DocumentNumber
          ? ` DA 4949 generated (doc #${result.da4949DocumentNumber}).`
          : '';
        const successMessage = `Processed ${result.processedBinIds.length} pending type change item${result.processedBinIds.length === 1 ? '' : 's'} at ${formatDateTime(result.processedAt)}.${docSuffix}`;
        setSubmissionSuccess(successMessage);
        notifications.success('Generate 4949 completed', successMessage);
        return;
      }

      if (confirmSelection.mode === 'pending-return-to-inventory') {
        const selectedBinIds: string[] = [];

        pendingSelectedRows.forEach((row) => {
          if (row.id.startsWith('manual:')) return;
          const qty = clampQuantity(
            pendingQtyByRow[row.id] ?? row.availableCount,
            row.availableCount,
          );
          if (qty <= 0) return;
          selectedBinIds.push(...row.binIds.slice(0, qty));
        });

        if (!selectedBinIds.length) {
          handleCloseConfirm();
          setSubmissionError('Unable to resolve selected bins. Refresh and try again.');
          return;
        }

        const result = await returnToInventory({ binIds: selectedBinIds });
        handleCloseConfirm();
        setPendingSelectedRowIds([]);
        setPendingQtyByRow({});
        const returnedCount = result.updatedAssetIds.length;
        const skippedCount = result.skippedAssetIds?.length ?? 0;
        const resultWarnings = Array.isArray(result.warnings)
          ? result.warnings.filter(
              (msg): msg is string => typeof msg === 'string' && msg.trim().length > 0,
            )
          : [];
        if (skippedCount > 0 && resultWarnings.length > 0) {
          const warningText = Array.from(new Set(resultWarnings)).join(' ');
          setSubmissionWarning(warningText);
          notifications.warning('Return to Inventory completed with warnings', warningText);
        }
        const successMessage = `Returned ${returnedCount} item${returnedCount === 1 ? '' : 's'} to inventory at ${formatDateTime(result.processedAt)}.`
          + (skippedCount > 0 ? ` ${skippedCount} item(s) removed from queue without status change.` : '');
        setSubmissionSuccess(successMessage);
        notifications.success('Return to Inventory completed', successMessage);
        return;
      }

      if (confirmSelection.mode === 'pending-1348') {
        const selectedBinIds: string[] = [];
        let expectedBinCount = 0;

        // Collect bin IDs from real pending rows (non-manual)
        pendingSelectedRowsWithoutPending.forEach((row) => {
          if (row.id.startsWith('manual:')) return;
          const qty = clampQuantity(
            pendingQtyByRow[row.id] ?? row.availableCount,
            row.availableCount,
          );
          if (qty <= 0) {
            return;
          }
          expectedBinCount += qty;
          selectedBinIds.push(...row.binIds.slice(0, qty));
        });

        // Collect selected manual entries
        const selectedManualIds = new Set(
          pendingSelectedRowsWithoutPending
            .filter((row) => row.id.startsWith('manual:'))
            .map((row) => row.id.replace('manual:', '')),
        );
        const selectedManualItems = manualEntries
          .filter((item) => selectedManualIds.has(item.id))
          .map((item) => ({
            lin: item.lin,
            nsn: item.nsn,
            nomenclature: item.nomenclature,
            quantity: item.quantity,
            conditionCode: item.conditionCode,
            remarks: item.remarks,
          }));

        const hasBins = selectedBinIds.length > 0;
        const hasManual = selectedManualItems.length > 0;

        if (hasBins && selectedBinIds.length !== expectedBinCount) {
          handleCloseConfirm();
          if (dd1348Window && !dd1348Window.closed) dd1348Window.close();
          setSubmissionError(
            'Unable to resolve selected pending disposal bins. Refresh and try again.',
          );
          return;
        }

        if (!hasBins && !hasManual) {
          handleCloseConfirm();
          if (dd1348Window && !dd1348Window.closed) dd1348Window.close();
          setSubmissionError('Select at least one item to generate DD 1348-1A.');
          return;
        }

        // Build the request: binIds only, manualEntries only, or both
        const shippingFields = {
          ...(shipFromText.trim() ? { shipFrom: shipFromText.trim() } : {}),
          ...(shipToText.trim() ? { shipTo: shipToText.trim() } : {}),
        };
        const request = hasBins && hasManual
          ? { binIds: selectedBinIds, manualEntries: selectedManualItems, ...shippingFields }
          : hasBins
            ? { binIds: selectedBinIds, ...shippingFields }
            : { manualEntries: selectedManualItems, ...shippingFields };

        const result = await completeDisposal(request);
        handleCloseConfirm();
        setPendingSelectedRowIds([]);
        setPendingQtyByRow({});
        // Clear only the manual entries that were submitted
        if (hasManual) {
          selectedManualIds.forEach((id) => removeManualEntry(id));
        }
        if (result.dd1348Document?.generatedDocumentId) {
          await openDocumentWithAuth(
            `/api/v1/documents/${result.dd1348Document.generatedDocumentId}`,
            dd1348Window,
          );
        } else if (dd1348Window && !dd1348Window.closed) {
          dd1348Window.close();
        }
        const totalItems =
          result.disposedBinIds.length + selectedManualItems.reduce((sum, item) => sum + item.quantity, 0);
        const totalPages = result.dd1348PageCount ?? 0;
        const pendingDocSuffix = result.dd1348Document
          ? ` DD 1348-1A generated (${totalPages || 1} page${(totalPages || 1) === 1 ? '' : 's'}).`
          : '';
        const pendingSuccessMessage = `Processed ${totalItems} item${totalItems === 1 ? '' : 's'} at ${formatDateTime(result.processedAt)}.${pendingDocSuffix}`;
        setSubmissionSuccess(pendingSuccessMessage);
        notifications.success('Generate 1348 completed', pendingSuccessMessage);
        return;
      }

      const selectedAssetIds: string[] = [];
      let expectedCount = 0;
      const conditionCodeMap: Record<string, string> = {};

      filteredStockRows.forEach((row) => {
        if (!hasSelection(stockSelectedRowIds, row.id)) {
          return;
        }

        const qty = clampQuantity(stockQtyByRow[row.id] ?? row.availableCount, row.availableCount);
        if (qty <= 0) {
          return;
        }

        expectedCount += qty;
        const slicedIds = row.assetIds.slice(0, qty);
        selectedAssetIds.push(...slicedIds);

        if (row.conditionCode) {
          for (const assetId of slicedIds) {
            conditionCodeMap[assetId] = row.conditionCode;
          }
        }
      });

      if (!selectedAssetIds.length || selectedAssetIds.length !== expectedCount) {
        handleCloseConfirm();
        if (dd1348Window && !dd1348Window.closed) dd1348Window.close();
        setSubmissionError('Unable to resolve selected in-stock assets. Refresh and try again.');
        return;
      }

      const result = await completeDisposal({
        assetIds: selectedAssetIds,
        ...(Object.keys(conditionCodeMap).length > 0 ? { conditionCodeMap } : {}),
        ...(shipFromText.trim() ? { shipFrom: shipFromText.trim() } : {}),
        ...(shipToText.trim() ? { shipTo: shipToText.trim() } : {}),
      });
      handleCloseConfirm();
      setStockSelectedRowIds([]);
      setStockQtyByRow({});
      setStockConditionCodeByRow({});
      if (result.dd1348Document?.generatedDocumentId) {
        await openDocumentWithAuth(
          `/api/v1/documents/${result.dd1348Document.generatedDocumentId}`,
          dd1348Window,
        );
      } else if (dd1348Window && !dd1348Window.closed) {
        dd1348Window.close();
      }
      const stockTotalPages = result.dd1348PageCount ?? 0;
      const stockDocSuffix = result.dd1348Document
        ? ` DD 1348-1A generated (${stockTotalPages || 1} page${(stockTotalPages || 1) === 1 ? '' : 's'}).`
        : '';
      const stockSuccessMessage = `Processed ${result.disposedAssetIds.length} in-stock asset${result.disposedAssetIds.length === 1 ? '' : 's'} at ${formatDateTime(result.processedAt)}.${stockDocSuffix}`;
      setSubmissionSuccess(stockSuccessMessage);
      notifications.success('Generate 1348 completed', stockSuccessMessage);
    } catch (mutationError) {
      handleCloseConfirm();
      if (da4949Window && !da4949Window.closed) da4949Window.close();
      if (dd1348Window && !dd1348Window.closed) dd1348Window.close();
      const message = getErrorMessage(mutationError);
      setSubmissionError(message);
      if (confirmSelection.mode === 'pending-4949') {
        notifications.error('Generate 4949 failed', message);
      } else if (confirmSelection.mode === 'pending-return-to-inventory') {
        notifications.error('Return to Inventory failed', message);
      } else {
        notifications.error('Generate 1348 failed', message);
      }
    }
  }, [
    adjustPendingType,
    clearFeedback,
    completeDisposal,
    confirmSelection,
    filteredStockRows,
    handleCloseConfirm,
    justificationText,
    manualEntries,
    notifications,
    pendingQtyByRow,
    pendingSelectedRows,
    pendingSelectedRowsWithPending,
    pendingSelectedRowsWithoutPending,
    removeManualEntry,
    returnToInventory,
    shipFromText,
    shipToText,
    stockQtyByRow,
    stockSelectedRowIds,
  ]);

  const activeError = activeTab === 'pending' ? pendingError : stockError;

  if (activeTab === 'pending' && isPendingLoading) {
    return <LoadingSkeleton variant="grid" count={8} />;
  }

  if (activeTab === 'stock' && isStockLoading) {
    return <LoadingSkeleton variant="grid" count={8} />;
  }

  return (
    <Container maxWidth={false} sx={{ mt: 3, mb: 4 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Bulk Dispose
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Process disposals from pending bin queue items or directly from available in-stock assets.
      </Typography>

      {activeError ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {activeTab === 'pending'
            ? 'Unable to load pending disposal records. Please try again.'
            : 'Unable to load in-stock assets. Please try again.'}
        </Alert>
      ) : null}

      <Stack spacing={2}>
        <Paper elevation={1} sx={{ px: 2, pt: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, value: DisposeTab) => {
                setActiveTab(value);
              }}
              sx={{ flex: 1 }}
            >
              <Tab label="Pending Bin Queue" value="pending" />
              <Tab label="From Stock" value="stock" />
            </Tabs>
            {activeTab === 'pending' && (
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={() => setManualEntryDialogOpen(true)}
                  disabled={isSubmitting}
                >
                  Add Manual Entry
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PrintIcon fontSize="small" />}
                  onClick={handlePrintPendingQueue}
                >
                  Print Summary
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>

        {submissionError ? <Alert severity="error">{submissionError}</Alert> : null}
        {submissionWarning ? <Alert severity="warning">{submissionWarning}</Alert> : null}
        {conditionUpdateError ? <Alert severity="error">{conditionUpdateError}</Alert> : null}
        {submissionSuccess ? <Alert severity="success">{submissionSuccess}</Alert> : null}

        {activeTab === 'pending' ? (
          <PendingDisposeTab
            rows={filteredPendingRows}
            isFetching={isPendingFetching}
            assetNameFilter={pendingAssetNameFilter}
            linNsnFilter={pendingLinNsnFilter}
            fromDate={pendingFromDate}
            toDate={pendingToDate}
            selectedRowIds={pendingSelectedRowIds}
            qtyByRow={pendingQtyByRow}
            conditionUpdatePendingByRow={pendingConditionUpdateByRow}
            pendingChangesOnly={pendingChangesOnly}
            selectedQty={pendingSelectedQty}
            selectedPendingTypeChangeQty={pendingSelectedPendingTypeChangeQty}
            totalQty={pendingTotalQty}
            isSubmitting={isSubmitting}
            isUpdatingPendingTypeChange={isUpdatingPendingTypeChange}
            onAssetNameFilterChange={setPendingAssetNameFilter}
            onLinNsnFilterChange={setPendingLinNsnFilter}
            onFromDateChange={setPendingFromDate}
            onToDateChange={setPendingToDate}
            onPendingChangesOnlyChange={setPendingChangesOnly}
            onClearFilters={() => {
              setPendingAssetNameFilter('');
              setPendingLinNsnFilter('');
              setPendingFromDate('');
              setPendingToDate('');
              setPendingChangesOnly(false);
            }}
            onQtyChange={handlePendingQtyChange}
            onConditionCodeChange={handlePendingConditionCodeChange}
            onOpenAdjustType={handleOpenAdjustType}
            onSelectAllVisible={handlePendingSelectAllVisible}
            onDeselectAllVisible={handlePendingDeselectAll}
            onSelectionChange={handlePendingSelectionChange}
            onOpenConfirm1348={handleOpenPending1348Confirm}
            onOpenConfirm4949={handleOpenPending4949Confirm}
            onReturnToInventory={handleOpenReturnToInventoryConfirm}
            onRemoveManualEntry={removeManualEntry}
          />
        ) : activeTab === 'stock' ? (
          <StockDisposeTab
            rows={filteredStockRows}
            isFetching={isStockFetching}
            searchFilter={stockSearchFilter}
            selectedRowIds={stockSelectedRowIds}
            qtyByRow={stockQtyByRow}
            selectedQty={stockSelectedQty}
            totalQty={stockTotalQty}
            isCompleting={isSubmitting}
            onSearchFilterChange={setStockSearchFilter}
            onClearFilters={() => setStockSearchFilter('')}
            onQtyChange={handleStockQtyChange}
            onConditionCodeChange={handleStockConditionCodeChange}
            onSelectionChange={handleStockSelectionChange}
            onOpenConfirm={handleOpenStock1348Confirm}
          />
        ) : null}
      </Stack>

      <Dialog open={confirmOpen} onClose={handleCloseConfirm} fullWidth maxWidth="md">
        <DialogTitle>{confirmSelection?.title ?? 'Confirm'}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {confirmSelection?.description}
          </Typography>
          <Stack spacing={1.5}>
            {(confirmSelection?.rows ?? []).map((row) => (
              <Box
                key={row.id}
                sx={{
                  px: 1.5,
                  py: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2">{row.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.subtitle}
                </Typography>
              </Box>
            ))}
          </Stack>
          {confirmSelection?.mode === 'pending-4949' && (
            <TextField
              label="Justification"
              placeholder="Enter justification for DA 4949"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              value={justificationText}
              onChange={(e) => setJustificationText(e.target.value)}
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            />
          )}
          {(confirmSelection?.mode === 'pending-1348' || confirmSelection?.mode === 'stock-1348') && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Ship From"
                placeholder="Ship from address (optional)"
                fullWidth
                value={shipFromText}
                onChange={(e) => setShipFromText(e.target.value)}
                disabled={isSubmitting}
                size="small"
              />
              <TextField
                label="Ship To"
                placeholder="Ship to address (optional)"
                fullWidth
                value={shipToText}
                onChange={(e) => setShipToText(e.target.value)}
                disabled={isSubmitting}
                size="small"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={isSubmitting}>
            Cancel
          </Button>
          <ViewerGuardedButton
            onClick={handleConfirm}
            variant="contained"
            color={confirmSelection?.confirmColor ?? 'primary'}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : (confirmSelection?.confirmLabel ?? 'Confirm')}
          </ViewerGuardedButton>
        </DialogActions>
      </Dialog>

      <AdjustPendingTypeDialog
        open={Boolean(adjustTypeRow)}
        row={adjustTypeRow}
        isSubmitting={isUpdatingPendingTypeChange}
        onClose={() => setAdjustTypeRow(null)}
        onSave={handleSavePendingTypeChange}
        onClear={handleClearPendingTypeChange}
      />

      <AddManualEntryDialog
        open={manualEntryDialogOpen}
        onClose={() => setManualEntryDialogOpen(false)}
        onAdd={addManualEntry}
      />
    </Container>
  );
};
